import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../hooks';
import { booksService } from '../../services/api';
import { readingSessionsService } from '../../services/api/reading-sessions.service';
import { Zone, Book } from '../../types';
import { LoadingSpinner, LineChart } from '../../components/common';
import { RefreshCw, DownloadCloud } from 'lucide-react';

const groupOptions = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' },
] as const;

const zones = [
  { label: 'All Zones', value: '' },
  { label: 'South East', value: Zone.SE },
  { label: 'South West', value: Zone.SW },
  { label: 'Mid Ghana', value: Zone.MG },
  { label: 'Northern', value: Zone.N },
];

const formatDuration = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hrs > 0 ? `${hrs}h` : '', mins > 0 ? `${mins}m` : '', `${secs}s`]
    .filter(Boolean)
    .join(' ');
};

export const ReadingStatsPage: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const [books, setBooks] = useState<Book[]>([]);
  const [bookId, setBookId] = useState('');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [zone, setZone] = useState<string>('');
  const [summary, setSummary] = useState<any[]>([]);
  const [totals, setTotals] = useState({ totalDurationSeconds: 0, sessions: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadBooks = useCallback(async () => {
    try {
      const data = await booksService.getBooks({ limit: 100 });
      setBooks(data.books);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await readingSessionsService.getSummary({ groupBy, zone: zone || undefined, bookId: bookId || undefined, limit: 30 });
      setSummary(data.summary);
      setTotals({ totalDurationSeconds: data.totalDurationSeconds, sessions: data.sessions });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load reading statistics');
    } finally {
      setIsLoading(false);
    }
  }, [groupBy, zone, bookId]);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleRefresh = () => loadSummary();

  const handleExportCSV = () => {
    if (!summary || summary.length === 0) return;
    const rows = [['Period', 'ReadingTimeSeconds', 'Sessions'], ...summary.map((r) => [r.period, String(r.totalDurationSeconds), String(r.sessions)])];
    const csv = rows.map((r) => r.map((c) => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reading-summary-${groupBy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const chartData = summary.map((row) => ({
    label: row.period,
    value: Math.round(row.totalDurationSeconds / 60),
  }));

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="overflow-hidden rounded-[2rem] bg-gradient-to-r from-sky-700 via-cyan-600 to-indigo-700 shadow-2xl text-white mb-10">
        <div className="p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm uppercase tracking-[0.28em] text-cyan-100/80">Admin dashboard</p>
              <h1 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">Reading Statistics</h1>
              <p className="mt-3 max-w-2xl text-sm sm:text-base text-slate-100/85">Track reading time by book, zone, and period. Use high-level trends and details to monitor reading activity and control performance.</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={handleExportCSV} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-slate-900/10 transition hover:bg-slate-100">
                <DownloadCloud size={16} /> Export
              </button>
              <button onClick={handleRefresh} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition hover:bg-white/20">
                <RefreshCw size={16} /> Refresh
              </button>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-3xl bg-white/10 p-5 shadow-xl backdrop-blur-sm border border-white/10">
              <p className="text-sm text-cyan-100">Total Reading Time</p>
              <p className="mt-5 text-3xl font-semibold text-white">{formatDuration(totals.totalDurationSeconds)}</p>
              <p className="mt-3 text-sm text-cyan-100/80">Overall duration for selected sessions</p>
            </div>
            <div className="rounded-3xl bg-white/10 p-5 shadow-xl backdrop-blur-sm border border-white/10">
              <p className="text-sm text-cyan-100">Total Sessions</p>
              <p className="mt-5 text-3xl font-semibold text-white">{totals.sessions}</p>
              <p className="mt-3 text-sm text-cyan-100/80">Count of reading sessions included</p>
            </div>
            <div className="rounded-3xl bg-white/10 p-5 shadow-xl backdrop-blur-sm border border-white/10">
              <p className="text-sm text-cyan-100">Filtered Zone</p>
              <p className="mt-5 text-2xl font-semibold text-white">{zone ? zones.find((item) => item.value === zone)?.label : 'All Zones'}</p>
              <p className="mt-3 text-sm text-cyan-100/80">Active zone filter for the report</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <div className="rounded-3xl bg-white p-4 shadow-sm border border-slate-200">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Group by</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as any)}
              className="mt-2 block w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-3 text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-200"
            >
              {groupOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded-3xl bg-white p-4 shadow-sm border border-slate-200">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Zone</span>
            <select
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              className="mt-2 block w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-3 text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-200"
            >
              {zones.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded-3xl bg-white p-4 shadow-sm border border-slate-200">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Book</span>
            <select
              value={bookId}
              onChange={(e) => setBookId(e.target.value)}
              className="mt-2 block w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-3 text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-sky-200"
            >
              <option value="">All Books</option>
              {books.map((book) => (
                <option key={book._id} value={book._id}>{book.title}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-6 shadow-xl mb-6 border border-slate-200">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Reading time trend</h2>
            <p className="text-sm text-slate-500 mt-1">Visualize recent reading time across the selected filters.</p>
          </div>
          <div className="inline-flex items-center rounded-full bg-sky-100 px-4 py-2 text-sm font-medium text-sky-700">
            {groupOptions.find((option) => option.value === groupBy)?.label} trend
          </div>
        </div>
        <div className="mt-6 min-h-[280px]">
          {summary.length > 1 ? (
            <LineChart data={chartData} className="h-[280px]" />
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">
              Add more reading sessions to visualize your trend.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm overflow-x-auto border border-slate-100">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-sm text-slate-500 border-b border-slate-100">
                <th className="py-3 pr-6 px-2 uppercase tracking-wide">Period</th>
                <th className="py-3 pr-6 px-2 uppercase tracking-wide">Reading Time</th>
                <th className="py-3 pr-6 px-2 uppercase tracking-wide">Sessions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-slate-500">No reading sessions found for the selected filters.</td>
                </tr>
              ) : (
                summary.map((row) => (
                  <tr key={row.period} className="text-sm text-slate-700">
                    <td className="py-3 pr-6 px-2 font-medium">{row.period}</td>
                    <td className="py-3 pr-6 px-2">{formatDuration(row.totalDurationSeconds)}</td>
                    <td className="py-3 pr-6 px-2">{row.sessions}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
